import React, { useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTicket,
  faUtensils,
  faMugHot,
  faMicrophone,
  faVolumeUp,
  faPlane,
  faHotel,
  faCalendar,
  faInfoCircle,
  faCheckCircle,
  faCircle,
  faEdit,
  faTrash,
  faEllipsisV,
  faMapMarkerAlt,
  faArrowDown,
  faArrowRight,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import UserContext from '../../context/UserContext';
import styles from './ScheduleCard.module.css';

const ScheduleCard = ({ schedule, eventId, dateId, eventType = 'tour', onUpdate, onEdit, onDelete, contacts = [] }) => {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [showOptions, setShowOptions] = useState(false);

  const getIconName = (title) => {
    const lowercaseTitle = title.toLowerCase();
  
    const concertKeywords = ['festival', 'gig', 'show', 'concert'];
    if (concertKeywords.some(keyword => lowercaseTitle.includes(keyword))) {
      return faTicket;
    }
  
    const mealKeywords = ['dinner', 'food', 'lunch', 'breakfast', 'brunch'];
    if (mealKeywords.some(keyword => lowercaseTitle.includes(keyword))) {
      return faUtensils;
    }
  
    const snackKeywords = ['snacks', 'high tea', 'tea break', 'tea', 'coffee', 'snack break', 'break', 'eat'];
    if (snackKeywords.some(keyword => lowercaseTitle.includes(keyword))) {
      return faMugHot;
    }
  
    const soundcheckKeywords = ['soundcheck', 'test', 'test run'];
    if (soundcheckKeywords.some(keyword => lowercaseTitle.includes(keyword))) {
      return faMicrophone;
    }
  
    const rehearsalKeywords = ['jam', 'rehearsal', 'practice', 'jam session'];
    if (rehearsalKeywords.some(keyword => lowercaseTitle.includes(keyword))) {
      return faVolumeUp;
    }
  
    const flightKeywords = ['flight', 'airport', 'plane', 'fly'];
    if (flightKeywords.some(keyword => lowercaseTitle.includes(keyword))) {
      return faPlane;
    }
  
    const hotelKeywords = ['hotel', 'hostel', 'accommodation', 'checkin', 'checkout'];
    if (hotelKeywords.some(keyword => lowercaseTitle.includes(keyword))) {
      return faHotel;
    }
  
    return faCalendar;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    
    // Create a date object with the hours and minutes
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (e) {
      console.error("Invalid date format:", dateStr);
      return '';
    }
  };

  const isVisible = () => {
    // Always show all schedules to Managers
    if (userData.userType === 'Manager') {
      return true;
    }
  
    // Show to creator
    if (userData.userId === schedule.creatorId) {
      return true;
    }
  
    // Check other visibility conditions
    switch (schedule.visibilityType) {
      case 'me':
        return userData.userId === schedule.creatorId;
      case 'custom':
        return Array.isArray(schedule.visibility) && schedule.visibility.includes(userData.userId);
      case 'all':
        return true;
      default:
        // For artist, crew, etc.
        return schedule.visibilityType === userData.userType.toLowerCase();
    }
  };

  const handleCardClick = (e) => {
    // Don't navigate if clicking on action buttons
    if (e.target.closest(`.${styles.actionButton}`) || 
        e.target.closest(`.${styles.optionsButton}`) ||
        e.target.closest(`.${styles.optionsMenu}`)) {
      return;
    }
    
    // Use docId instead of id for navigation
    navigate(`/${eventType}/${eventId}/${dateId}/schedule/${schedule.docId}`);
  };
  
  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit(schedule);
    setShowOptions(false);
  };
  
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      // Use docId instead of id for deletion
      if (onDelete) onDelete(schedule.docId);
    }
    setShowOptions(false);
  };
  
  const toggleOptions = (e) => {
    e.stopPropagation();
    setShowOptions(!showOptions);
  };
  
  const handleClickOutside = () => {
    setShowOptions(false);
  };

  if (!isVisible()) return null;

  // Check if the schedule has start date and end date (new format)
  const hasDateInfo = schedule.startDate && schedule.endDate;
  const isSameDay = hasDateInfo && 
    new Date(schedule.startDate).toDateString() === new Date(schedule.endDate).toDateString();

  // Get assigned contacts for this schedule
  const assignedContacts = schedule.assignedContacts ? 
    contacts.filter(contact => schedule.assignedContacts.includes(contact.id)) : [];

  return (
    <div className={styles['schedule-card']} onClick={handleCardClick}>
      <div className={styles['schedule-card-content']}>
        
        {/* Header Section */}
        <div className={styles['schedule-header']}>
          <div className={styles['schedule-title-section']}>
            <h3 className={styles['schedule-title']}>{schedule.title}</h3>
            {hasDateInfo && (
              <p className={styles['schedule-date']}>
                {formatDate(schedule.startDate)}
                {!isSameDay && ` - ${formatDate(schedule.endDate)}`}
              </p>
            )}
          </div>
          
          <div className={styles['schedule-status']}>
            <FontAwesomeIcon 
              icon={schedule.isConfirmed ? faCheckCircle : faCircle}
              className={`${styles['status-icon']} ${schedule.isConfirmed ? styles.confirmed : styles.unconfirmed}`}
            />
            
            {/* Options button (visible to creator or manager) */}
            {(userData.userId === schedule.creatorId || userData.userType === 'Manager') && (
              <div className={styles.optionsContainer}>
                <button
                  className={styles.optionsButton}
                  onClick={toggleOptions}
                  aria-label="Options"
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </button>
                
                {showOptions && (
                  <div className={styles.optionsMenu}>
                    <button className={styles.optionItem} onClick={handleEditClick}>
                      <FontAwesomeIcon icon={faEdit} />
                      <span>Edit</span>
                    </button>
                    <button className={styles.optionItem} onClick={handleDeleteClick}>
                      <FontAwesomeIcon icon={faTrash} />
                      <span>Delete</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Time Section */}
        <div className={styles['schedule-time-section']}>
          <div className={styles['schedule-time']}>
            {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
          </div>
        </div>

        {/* Locations Section */}
        {(schedule.fromLocation || schedule.toLocation) && (
          <div className={styles['schedule-locations']}>
            {schedule.fromLocation && (
              <div className={styles['location-row']}>
                <div className={`${styles['location-icon']} ${styles.from}`}>
                  FROM
                </div>
                <div className={styles['location-details']}>
                  <div className={styles['location-name']}>
                    {schedule.fromLocation.displayName}
                  </div>
                </div>
              </div>
            )}

            {schedule.fromLocation && schedule.toLocation && (
              <div className={styles['journey-line']}>
                <div className={styles['journey-icon']}>
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              </div>
            )}

            {schedule.toLocation && (
              <div className={styles['location-row']}>
                <div className={`${styles['location-icon']} ${styles.to}`}>
                  TO
                </div>
                <div className={styles['location-details']}>
                  <div className={styles['location-name']}>
                    {schedule.toLocation.displayName}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes Section */}
        {schedule.notes && (
          <div className={styles['schedule-notes']}>
            <p className={styles['schedule-notes-text']}>
              {schedule.notes}
            </p>
          </div>
        )}

        {/* Footer Section */}
        <div className={styles['schedule-footer']}>
          <div className={styles['schedule-footer-left']}>
            <span className={styles['visibility-badge']}>
              {schedule.visibilityType ? schedule.visibilityType.charAt(0).toUpperCase() + schedule.visibilityType.slice(1) : 'All'}
            </span>
            
            {/* Contact badge */}
            {assignedContacts.length > 0 && (
              <span className={styles['contact-badge']}>
                {assignedContacts.length} Contact{assignedContacts.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          
          <button className={styles['details-button']} onClick={handleCardClick}>
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>Details</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleCard;